<script setup>
import { computed, ref } from 'vue';
import BaseBubble from 'next/message/bubbles/Base.vue';
import FormattedContent from './FormattedContent.vue';
import AttachmentChips from 'next/message/chips/AttachmentChips.vue';
import TranslationToggle from 'dashboard/components-next/message/TranslationToggle.vue';
import { MESSAGE_TYPES } from '../../constants';
import { useMessageContext } from '../../provider.js';
import { useTranslations } from 'dashboard/composables/useTranslations';

const { content, attachments, contentAttributes, messageType } =
  useMessageContext();

const { hasTranslations, translationContent } =
  useTranslations(contentAttributes);

const renderOriginal = ref(false);

// SZ_GROUP_SENDER_HEADER_v1_20260520: quando a mensagem é de grupo (sender_jid
// presente), o header com nome/fone do remetente é renderizado pelo
// SzGroupSenderHeader dentro do BaseBubble. Strippa o prefixo "*Nome*:" do
// conteúdo pra evitar duplicação visual.
const stripGroupSenderPrefix = raw => {
  if (!raw) return raw;
  // Chatwoot front converte keys do backend para camelCase nas props
  const hasJid =
    contentAttributes.value?.senderJid || contentAttributes.value?.sender_jid;
  if (!hasJid) return raw;
  return raw.replace(/^\*[^*\n]+\*\s*:\s*\n?/, '');
};

const renderContent = computed(() => {
  if (renderOriginal.value) {
    return stripGroupSenderPrefix(content.value);
  }

  if (hasTranslations.value) {
    return stripGroupSenderPrefix(translationContent.value);
  }

  return stripGroupSenderPrefix(content.value);
});

const isTemplate = computed(() => {
  return messageType.value === MESSAGE_TYPES.TEMPLATE;
});

const isEmpty = computed(() => {
  return !content.value && !attachments.value?.length;
});

const handleSeeOriginal = () => {
  renderOriginal.value = !renderOriginal.value;
};
</script>

<template>
  <BaseBubble class="px-4 py-3" data-bubble-name="text">
    <div class="gap-3 flex flex-col">
      <span v-if="isEmpty" class="text-n-slate-11">
        {{ $t('CONVERSATION.NO_CONTENT') }}
      </span>
      <FormattedContent v-if="renderContent" :content="renderContent" />
      <TranslationToggle
        v-if="hasTranslations"
        class="-mt-3"
        :showing-original="renderOriginal"
        @toggle="handleSeeOriginal"
      />
      <AttachmentChips :attachments="attachments" class="gap-2" />
      <template v-if="isTemplate">
        <div
          v-if="contentAttributes.submittedEmail"
          class="px-2 py-1 rounded-lg bg-n-alpha-3"
        >
          {{ contentAttributes.submittedEmail }}
        </div>
      </template>
    </div>
  </BaseBubble>
</template>

<style>
p:last-child {
  margin-bottom: 0;
}
</style>
